/**
 * Consentimento de cookies (LGPD/GDPR) + integração com o Google Consent
 * Mode v2. O estado default ('denied') é setado no index.html antes do
 * gtag config; aqui só lemos/gravamos a escolha do usuário e propagamos
 * o `gtag('consent','update')` para GA e AdSense.
 *
 * Storage: cookie com Domain=.bolaoelite.com.br (mesma estratégia do cookie
 * de idioma) pra que a escolha valha em todos os subdomínios — landing
 * (`dev.bolaoelite.com.br`) compartilha com a área logada
 * (`dev.app.bolaoelite.com.br`). Sem isso, o banner aparecia duas vezes
 * (uma por host, pois localStorage é per-origin).
 *
 * Nome do cookie diferenciado por ambiente (env build-time) pra escolha em
 * DEV não vazar pra PROD nem vice-versa: `cookie-consent-dev` em DEV,
 * `cookie-consent` em PROD/local.
 */

import { COOKIE_DOMAIN, LANG_COOKIE_NAME } from '../i18n';

export type ConsentValue = 'granted' | 'denied';

/**
 * Nome do cookie derivado do `LANG_COOKIE_NAME` (mesma convenção PROD/DEV).
 * Ex.: `bolao-lang-dev` → `cookie-consent-dev`; `bolao-lang` → `cookie-consent`.
 */
const COOKIE_NAME = LANG_COOKIE_NAME.endsWith('-dev') ? 'cookie-consent-dev' : 'cookie-consent';
/** 1 ano de retenção — mesmo padrão do Consent Mode. */
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const t = part.trim();
    if (t.startsWith(prefix)) return decodeURIComponent(t.slice(prefix.length));
  }
  return null;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  const domainAttr = COOKIE_DOMAIN ? `; Domain=.${COOKIE_DOMAIN}` : '';
  const secureAttr = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${name}=${encodeURIComponent(value)}` +
    `; Path=/${domainAttr}` +
    `; Max-Age=${MAX_AGE_SECONDS}` +
    `; SameSite=Lax${secureAttr}`;
}

/** Escolha persistida do usuário, ou null se ele ainda não decidiu. */
export function getStoredConsent(): ConsentValue | null {
  // Cookie é a fonte canônica (cross-subdomain).
  const fromCookie = readCookie(COOKIE_NAME);
  if (fromCookie === 'granted' || fromCookie === 'denied') return fromCookie;

  // Fallback de migração: lê do localStorage antigo na primeira carga e
  // promove pro cookie. Limpa o LS depois pra evitar drift.
  try {
    const legacy = localStorage.getItem('cookie-consent');
    if (legacy === 'granted' || legacy === 'denied') {
      writeCookie(COOKIE_NAME, legacy);
      localStorage.removeItem('cookie-consent');
      return legacy;
    }
  } catch {
    /* localStorage indisponível */
  }

  return null;
}

/**
 * Persiste a escolha e atualiza o Consent Mode. `granted` libera analytics
 * e anúncios; `denied` mantém tudo bloqueado (GA roda em modo cookieless).
 */
export function setConsent(value: ConsentValue): void {
  writeCookie(COOKIE_NAME, value);
  // Limpa qualquer resíduo legado pra não confundir.
  try {
    localStorage.removeItem('cookie-consent');
  } catch {
    /* segue sem persistir LS */
  }

  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag === 'function') {
    gtag('consent', 'update', {
      ad_storage: value,
      ad_user_data: value,
      ad_personalization: value,
      analytics_storage: value,
    });
  }
}
